import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { countries } from '../form-data/formOptions';

interface CountryTypeaheadProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const TOP_COUNTRIES = [
  'United States',
  'United Kingdom',
  'Germany',
  'France',
  'Switzerland',
  'Spain',
  'Italy',
  'Netherlands',
];

export function CountryTypeahead({ value, onChange, disabled }: CountryTypeaheadProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuStyle, setMenuStyle] = useState<{ top: number; left: number; width: number } | null>(
    null,
  );
  const [placeAbove, setPlaceAbove] = useState(false);

  // Do not auto-select a country to avoid unintended preselection

  const all = useMemo(() => countries.map((c) => c.value), []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((name) => name.toLowerCase().includes(q));
  }, [query, all]);

  const topSuggestions = useMemo(() => {
    const set = new Set(TOP_COUNTRIES);
    const list = all.filter((c) => set.has(c));
    return list;
  }, [all]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const container = containerRef.current;
      const menu = menuRef.current;
      if (!container) return;
      // If click is inside the input container or inside the menu, do nothing
      if (container.contains(target) || (menu && menu.contains(target))) return;
      // Otherwise close the menu
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Compute and update portal menu position
  const updateMenuPosition = () => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();

    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;
    const spaceBelow = viewportH - rect.bottom;
    const spaceAbove = rect.top;
    const shouldFlip = spaceBelow < 200 && spaceAbove > spaceBelow;
    setPlaceAbove(shouldFlip);

    // Clamp horizontal position and width to viewport with margins
    const margin = 8;
    const clampedWidth = Math.min(rect.width, viewportW - margin * 2);
    const clampedLeft = Math.max(margin, Math.min(rect.left, viewportW - clampedWidth - margin));

    // Clamp vertical anchor within viewport margins
    const anchor = shouldFlip
      ? Math.max(margin, rect.top)
      : Math.min(rect.bottom, viewportH - margin);

    setMenuStyle({
      top: anchor,
      left: clampedLeft,
      width: clampedWidth,
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const onScroll = () => updateMenuPosition();
    const onResize = () => updateMenuPosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  const commit = (val: string) => {
    onChange(val);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#E34290] focus:border-[#E34290]"
        placeholder="Start typing your country"
        disabled={disabled}
        value={query || value}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {open &&
        menuStyle &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: 'fixed',
              top: placeAbove ? menuStyle.top - 8 : menuStyle.top + 8,
              left: menuStyle.left,
              width: menuStyle.width,
              zIndex: 1000,
              maxHeight: placeAbove
                ? Math.max(160, Math.min(320, menuStyle.top - 16))
                : Math.max(160, Math.min(320, window.innerHeight - menuStyle.top - 16)),
            }}
            className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl overflow-auto overscroll-contain text-gray-900 dark:text-gray-100"
          >
            {!query && (
              <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-300">Popular</div>
            )}
            {!query && (
              <div className="px-2 pb-2 grid grid-cols-2 gap-2">
                {topSuggestions.map((name) => (
                  <button
                    key={name}
                    type="button"
                    className="px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 text-left hover:border-[#E34290] hover:text-[#E34290] text-gray-900 dark:text-gray-100"
                    onClick={() => commit(name)}
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {filtered.map((name) => (
                <button
                  key={name}
                  type="button"
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-900 dark:text-gray-100"
                  onClick={() => commit(name)}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
